import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollToTop from '@/components/ScrollToTop';
import ViewportMeta from '@/components/ViewportMeta';
import ActivityBackButton from '@/components/ActivityBackButton';
import ActivityImageGallery from '@/components/ActivityImageGallery';
import ActivityVideoGallery from '@/components/ActivityVideoGallery';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDeviceFeatures } from '@/hooks/useResponsive';
import AnnathanamDonationForm from '@/components/AnnathanamDonationForm';
import { ACTIVITIES } from '@/data/activities';
import { useActivityCms } from '@/hooks/useActivityCms';
import { pickBilingual } from '@/services/contentApi';

const FALLBACK_GALLERY_IMAGES = ACTIVITIES.annathanam.galleryImages;

const Annathanam: React.FC = () => {
    const { t, language } = useLanguage();
    const { isMobile } = useDeviceFeatures();

    const [isGalleryExpanded, setIsGalleryExpanded] = useState(false);

    const { images: galleryImages, videoIds, content } = useActivityCms('annathanam', {
        images: FALLBACK_GALLERY_IMAGES,
        videoIds: ACTIVITIES.annathanam.youtubeVideoIds,
    });

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    const annathanamTitle = pickBilingual(content?.title, language, t('annathanam'));
    const overrideParagraphs = content?.paragraphs?.length
        ? content.paragraphs.map((p) => pickBilingual(p, language))
        : null;
    const captionText = pickBilingual(content?.caption, language, '');
    const hasCaption = captionText.trim() !== '';
    const galleryTitle = `${annathanamTitle} ${t('gallery')}`;
    const videoTitle = `${annathanamTitle} ${t('videos')}`;

    return (
        <>
            <ViewportMeta />
            <div className="min-h-screen flex flex-col">
                <Header />
                <ActivityBackButton
                    isExpanded={isGalleryExpanded}
                    onCloseExpanded={() => setIsGalleryExpanded(false)}
                />
                <main className="flex-grow pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
                    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
                        <div className="p-8 md:p-12">
                            <div className="text-center mb-10">
                                <span className="text-6xl mb-4 block">🍲</span>
                                <h1 className="text-4xl md:text-5xl font-bold text-[#1C3F75] font-serif mb-4">
                                    {annathanamTitle}
                                </h1>
                                <div className="h-1 w-24 bg-primary-500 mx-auto rounded-full"></div>
                            </div>

                            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                                {overrideParagraphs ? (
                                    overrideParagraphs.map((text, index) => (
                                        <p key={index} className="leading-relaxed text-left">
                                            {text}
                                        </p>
                                    ))
                                ) : (
                                    <>
                                        <p className="text-xl font-medium text-center text-[#1C3F75] italic mb-8">
                                            &ldquo;{t('annathanamQuote')}&rdquo;
                                        </p>

                                        <p className="leading-relaxed text-left">
                                            {t('annathanamDesc1')}
                                        </p>

                                        <p className="leading-relaxed text-left">
                                            {t('annathanamDesc2')}
                                            <br />
                                            <span className="font-semibold block mt-2 text-center">&ldquo;{t('annathanamQuote2')}&rdquo;</span>
                                        </p>

                                        <p className="leading-relaxed text-left">
                                            {t('annathanamDesc3')}
                                        </p>

                                        <p className="leading-relaxed text-left">
                                            {t('annathanamDesc4')}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="max-w-4xl mx-auto mt-8 mb-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                                    backgroundColor: '#FFFFFF'
                                }}
                            >
                                <thead>
                                    <tr style={{ backgroundColor: '#1C3F75' }}>
                                        <th
                                            style={{
                                                padding: isMobile ? '12px 10px' : '15px',
                                                textAlign: 'left',
                                                color: '#FFFFFF',
                                                fontWeight: 600,
                                                fontSize: isMobile ? '14px' : '16px',
                                                borderRight: '1px solid rgba(255, 255, 255, 0.2)'
                                            }}
                                        >
                                            Donation
                                        </th>
                                        <th
                                            style={{
                                                padding: isMobile ? '12px 10px' : '15px',
                                                textAlign: 'left',
                                                color: '#FFFFFF',
                                                fontWeight: 600,
                                                fontSize: isMobile ? '14px' : '16px'
                                            }}
                                        >
                                            Amount
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr style={{ backgroundColor: '#FFFFFF' }}>
                                        <td
                                            style={{
                                                padding: isMobile ? '12px 10px' : '15px',
                                                borderRight: '1px solid #E0E0E0',
                                                borderTop: '1px solid #E0E0E0',
                                                color: '#333333',
                                                fontSize: isMobile ? '14px' : '16px',
                                                fontWeight: 900
                                            }}
                                        >
                                            {t('lunchMassFeeding')}
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '12px 10px' : '15px',
                                                borderTop: '1px solid #E0E0E0',
                                                color: '#333333',
                                                fontSize: isMobile ? '14px' : '16px',
                                                fontWeight: 900
                                            }}
                                        >
                                            ₹ 6,000
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </motion.div>
                    </div>

                    <ActivityVideoGallery
                        videoIds={videoIds}
                        sectionTitle={videoTitle}
                        activityLabel={annathanamTitle}
                    />

                    <ActivityImageGallery
                        images={galleryImages}
                        galleryTitle={galleryTitle}
                        imageAltPrefix="Annathanam meal service"
                        onExpandedChange={setIsGalleryExpanded}
                        cardCaption={
                            hasCaption ? (
                                <p className="text-gray-600 font-semibold whitespace-pre-line">{captionText}</p>
                            ) : (
                                <div>
                                    <p className="text-gray-600 font-semibold">Location: Pallavaram</p>
                                    <p className="text-gray-500 text-xs mt-0.5">We Fed 200 people</p>
                                </div>
                            )
                        }
                        expandedCaption={
                            hasCaption ? (
                                <p className="font-semibold whitespace-pre-line">{captionText}</p>
                            ) : (
                                <>
                                    <p className="font-semibold">
                                        On 27/02/2025 &middot; Location: Pallavaram
                                    </p>
                                    <p className="text-sm text-gray-300 mt-0.5">We fed 200 people</p>
                                </>
                            )
                        }
                    />

                    <AnnathanamDonationForm />
                </main>
                <Footer />
                <ScrollToTop />
            </div>
        </>
    );
};

export default Annathanam;
